import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

export interface VirtualAccountInstruction {
  id: string;
  currency: string | null;
  sourceCurrency?: string | null; // Keep source_currency for backward compatibility
  status: string | null;
  paymentRail: string | null;
  paymentRails?: string[];
  bankName: string | null;
  bankAddress: string | null;
  beneficiaryName: string | null;
  beneficiaryAddress: string | null;
  accountNumber: string | null;
  routingNumber: string | null;
  wireRoutingNumber: string | null;
  accountType: string | null;
  iban: string | null;
  bic: string | null;
  clabe: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  reference: string | null;
  memo: string | null;
  destinationCurrency: string | null;
  destinationAddress: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// Full details for a single VA (unmasked)
export interface VirtualAccountFullDetails {
  id: string;
  bridgeVaId: string | null;
  currency: string;
  status: string | null;
  paymentRails: string[];
  // Bank
  bankName: string | null;
  bankAddress: string | null;
  // Beneficiary (CRITICAL)
  beneficiaryName: string | null;
  beneficiaryAddress: string | null;
  // SEPA/EUR
  iban: string | null;
  bic: string | null;
  // ACH/USD
  accountNumber: string | null;
  routingNumber: string | null;
  wireRoutingNumber: string | null;
  accountType: string | null;
  // SPEI/MXN
  clabe: string | null;
  // PIX/BRL
  pixKey: string | null;
  pixKeyType: string | null;
  // Reference
  reference: string | null;
  memo: string | null;
  // Destination
  destinationCurrency: string | null;
  destinationChain: string | null;
  // Limits
  limits: Record<string, any>;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface VirtualAccountResult {
  success: boolean;
  bridgeCustomerId: string | null;
  accounts: VirtualAccountInstruction[];
}

function mapInstruction(entry: Record<string, any>): VirtualAccountInstruction {
  // RPC returns both 'currency' and 'source_currency' - prefer 'currency' but fallback to 'source_currency'
  // RPC explicitly sets 'currency' = 'source_currency' in the query (line 41), so both should be the same
  // But we keep both for robustness and backward compatibility
  const currency = entry.currency || entry.source_currency;
  const sourceCurrency = entry.source_currency || entry.currency;
  
  // Log mapping for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('[useVirtualAccounts] Mapping instruction:', {
      id: entry.id,
      currency: entry.currency,
      source_currency: entry.source_currency,
      mappedCurrency: currency ? String(currency).toUpperCase() : null,
      mappedSourceCurrency: sourceCurrency ? String(sourceCurrency).toUpperCase() : null,
      status: entry.status,
    });
  }
  
  return {
    id: String(entry.id ?? ''),
    currency: currency ? String(currency).toUpperCase() : null, // Normalize to uppercase for consistent matching
    sourceCurrency: sourceCurrency ? String(sourceCurrency).toUpperCase() : null, // Keep for filtering
    status: entry.status ? String(entry.status) : null,
    paymentRail: entry.payment_rail ? String(entry.payment_rail) : null,
    paymentRails: Array.isArray(entry.payment_rails) ? entry.payment_rails : [],
    bankName: entry.bank_name ? String(entry.bank_name) : null,
    bankAddress: entry.bank_address ? String(entry.bank_address) : null,
    beneficiaryName: entry.beneficiary_name ? String(entry.beneficiary_name) : null,
    beneficiaryAddress: entry.beneficiary_address ? String(entry.beneficiary_address) : null,
    accountNumber: entry.account_number ? String(entry.account_number) : null,
    routingNumber: entry.routing_number ? String(entry.routing_number) : null,
    wireRoutingNumber: entry.wire_routing_number ? String(entry.wire_routing_number) : null,
    accountType: entry.account_type ? String(entry.account_type) : null,
    iban: entry.iban ? String(entry.iban) : null,
    bic: entry.bic ? String(entry.bic) : null,
    clabe: entry.clabe ? String(entry.clabe) : null,
    pixKey: entry.pix_key ? String(entry.pix_key) : null,
    pixKeyType: entry.pix_key_type ? String(entry.pix_key_type) : null,
    reference: entry.reference ? String(entry.reference) : null,
    memo: entry.memo ? String(entry.memo) : null,
    destinationCurrency: entry.destination_currency ? String(entry.destination_currency) : null,
    destinationAddress: entry.destination_address ? String(entry.destination_address) : null,
    createdAt: entry.created_at ? String(entry.created_at) : null,
    updatedAt: entry.updated_at ? String(entry.updated_at) : null,
  } satisfies VirtualAccountInstruction;
}

// Hook for fetching FULL (unmasked) details of a single VA
export function useVirtualAccountFullDetails(vaId: string | null) {
  return useQuery<VirtualAccountFullDetails | null>({
    queryKey: ['virtual-account-full', vaId],
    enabled: !!vaId,
    queryFn: async () => {
      if (!vaId) return null;
      
      const { data, error } = await supabase.rpc('user_virtual_account_full_details', {
        p_va_id: vaId
      });

      if (error) {
        throw error;
      }

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response');
      }

      const payload = data as Record<string, any>;

      if (!payload.ok) {
        throw new Error(payload.error || 'Failed to load account details');
      }

      const d = payload.data;
      
      return {
        id: String(d.id ?? ''),
        bridgeVaId: d.bridge_va_id ? String(d.bridge_va_id) : null,
        currency: d.currency ? String(d.currency).toUpperCase() : 'USD',
        status: d.status ? String(d.status) : null,
        paymentRails: Array.isArray(d.payment_rails) ? d.payment_rails : [],
        bankName: d.bank_name ? String(d.bank_name) : null,
        bankAddress: d.bank_address ? String(d.bank_address) : null,
        beneficiaryName: d.beneficiary_name ? String(d.beneficiary_name) : null,
        beneficiaryAddress: d.beneficiary_address ? String(d.beneficiary_address) : null,
        accountNumber: d.account_number ? String(d.account_number) : null,
        routingNumber: d.routing_number ? String(d.routing_number) : null,
        wireRoutingNumber: d.wire_routing_number ? String(d.wire_routing_number) : null,
        accountType: d.account_type ? String(d.account_type) : null,
        iban: d.iban ? String(d.iban) : null,
        bic: d.bic ? String(d.bic) : null,
        clabe: d.clabe ? String(d.clabe) : null,
        pixKey: d.pix_key ? String(d.pix_key) : null,
        pixKeyType: d.pix_key_type ? String(d.pix_key_type) : null,
        reference: d.reference ? String(d.reference) : null,
        memo: d.memo ? String(d.memo) : null,
        destinationCurrency: d.destination_currency ? String(d.destination_currency) : null,
        destinationChain: d.destination_chain ? String(d.destination_chain) : null,
        limits: d.limits || {},
        createdAt: d.created_at ? String(d.created_at) : null,
        updatedAt: d.updated_at ? String(d.updated_at) : null,
      } satisfies VirtualAccountFullDetails;
    },
  });
}

export function useVirtualAccounts(enabled: boolean = true) {
  return useQuery<VirtualAccountResult>({
    queryKey: ['virtual-accounts'],
    enabled,
    queryFn: async () => {
      // Use Supabase client directly for user-facing RPC (not admin facade)
      const { data, error } = await supabase.rpc('user_virtual_accounts_list');

      if (error) {
        throw error;
      }

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid virtual account payload');
      }

      const payload = data as Record<string, any>;

      // Handle RPC error response format
      if (payload.success === false) {
        throw new Error(payload.error || 'Failed to load virtual accounts');
      }

      const items = Array.isArray(payload.data) ? payload.data : [];

      return {
        success: payload.success === true,
        bridgeCustomerId: payload.bridge_customer_id ? String(payload.bridge_customer_id) : null,
        accounts: items
          .filter((entry): entry is Record<string, any> => entry && typeof entry === 'object')
          .map((entry) => mapInstruction(entry)),
      } satisfies VirtualAccountResult;
    },
  });
}

import { useState, useCallback, useMemo } from 'react'
// supabase already imported at the top of the file
import type {
  AdminVirtualAccount,
  AdminVirtualAccountsFilters,
  AdminVirtualAccountsList
} from '@/types/admin/virtualAccounts'

export type VirtualAccount = AdminVirtualAccount
export type VirtualAccountFilters = AdminVirtualAccountsFilters

export function useAdminVirtualAccounts(initialFilters: VirtualAccountFilters = {}) {
  const [data, setData] = useState<VirtualAccount[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [filters, setFilters] = useState<VirtualAccountFilters>(initialFilters)
  const [total, setTotal] = useState<number>(0)
  const [hasMore, setHasMore] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)

  const normalizeResponse = useCallback((payload: any): AdminVirtualAccountsList => {
    if (Array.isArray(payload)) {
      return { items: payload, total: payload.length, hasMore: false }
    }

    const items = Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.data)
        ? payload.data
        : []

    const normalizedItems = items
      .filter((entry): entry is Record<string, any> => entry && typeof entry === 'object')
      .map((entry) => {
        const email = entry.customer_email ?? null
        const fullName = entry.customer_name ?? email ?? 'Unknown'
        return {
          ...entry,
          bridge_va_id: entry.bridge_va_id ?? entry.id,
          source_currency: entry.source_currency ?? entry.currency ?? null,
          payment_rails: Array.isArray(entry.payment_rails) ? entry.payment_rails : [],
          bank_name: entry.bank_name ?? null,
          account_number_masked: entry.account_number_masked ?? entry.account_number ?? null,
          routing_number_masked: entry.routing_number_masked ?? entry.routing_number ?? null,
          iban_masked: entry.iban_masked ?? null,
          destination_address: entry.destination_address ?? null,
          customer: {
            id: entry.customer_id ?? '',
            email,
            first_name: null,
            last_name: null,
            full_name: fullName,
            kyc_status: entry.compliance_state ?? 'unknown',
            status: entry.customer_type ?? 'unknown'
          },
          synced_at: entry.synced_at ?? entry.updated_at ?? null,
          limits: entry.limits ?? null,
          compliance_state: entry.compliance_state ?? 'active',
          latest_deposit_at: entry.latest_deposit_at ?? null,
          latest_deposit_amount: entry.latest_deposit_amount ?? null
        }
      })

    const totalCount = typeof payload?.total === 'number'
      ? payload.total
      : typeof payload?.count === 'number'
        ? payload.count
        : items.length

    const hasMoreFlag = Boolean(payload?.hasMore ?? payload?.has_more ?? false)
    const lastSynced = payload?.lastSyncedAt ?? payload?.last_synced_at ?? payload?.updated_at ?? null

    return { items: normalizedItems, total: totalCount, hasMore: hasMoreFlag, lastSyncedAt: lastSynced }
  }, [])

  const fetchWithFilters = useCallback(async (payload: VirtualAccountFilters, append = false) => {
    setIsLoading(true)
    setError(null)

    try {
      const rpcFilters = {
        customer_id_param: payload.customer_id ?? null,
        status_param: payload.status && payload.status !== 'all' ? payload.status : null,
        currency_param: payload.currency ?? null,
        limit_param: payload.limit ?? 50,
        offset_param: payload.offset ?? 0
      }

      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_list_virtual_accounts', rpcFilters)

      if (rpcError) {
        throw new Error(rpcError.message || 'Failed to fetch virtual accounts')
      }

      const response = normalizeResponse(rpcData)

      setData(prev => (append ? [...prev, ...response.items] : response.items))
      setTotal(response.total)
      setHasMore(response.hasMore)
      setLastUpdatedAt(response.lastSyncedAt || new Date().toISOString())
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      if (!append) {
        setData([])
      }
    } finally {
      setIsLoading(false)
    }
  }, [normalizeResponse])

  const refetch = useCallback(() => {
    fetchWithFilters(filters)
  }, [fetchWithFilters, filters])

  const updateFilters = useCallback((newFilters: Partial<VirtualAccountFilters>) => {
    const nextFilters = { ...filters, ...newFilters, offset: 0 }
    setFilters(nextFilters)
    fetchWithFilters(nextFilters)
  }, [filters, fetchWithFilters])

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      const pageSize = filters.limit ?? 50
      const nextFilters = { ...filters, offset: (filters.offset ?? 0) + pageSize }
      setFilters(nextFilters)
      fetchWithFilters(nextFilters, true)
    }
  }, [hasMore, isLoading, filters, fetchWithFilters])

  const refresh = useCallback(() => {
    fetchWithFilters(filters)
  }, [fetchWithFilters, filters])

  const exportData = useCallback(async (format: 'csv' | 'json') => {
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_list_virtual_accounts', {
        customer_id_param: filters.customer_id ?? null,
        status_param: filters.status && filters.status !== 'all' ? filters.status : null,
        currency_param: filters.currency ?? null,
        limit_param: 1000,
        offset_param: 0
      })

      if (rpcError) {
        throw new Error(rpcError.message || `Failed to export ${format}`)
      }

      const parsed = normalizeResponse(rpcData).items
      const serialized = format === 'csv'
        ? parsed.map(item => JSON.stringify(item)).join('\n')
        : JSON.stringify(parsed)

      const blob = new Blob([serialized], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `virtual-accounts-${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error(`Export ${format} failed:`, err)
      throw err
    }
  }, [filters, normalizeResponse])

  const kpis = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    const todayAccounts = data.filter(account =>
      account.created_at.startsWith(today)
    )
    const activatedAccounts = data.filter(account =>
      account.status === 'activated' // Bridge API: activated | deactivated
    )

    // Currency breakdown (source = fiat input)
    const usdAccounts = data.filter(a => a.source_currency === 'usd')
    const eurAccounts = data.filter(a => a.source_currency === 'eur')
    const mxnAccounts = data.filter(a => a.source_currency === 'mxn')

    // Customer-linked accounts
    const customersWithAccounts = new Set(data.filter(a => a.customer_id).map(a => a.customer_id)).size

    return [
      {
        label: 'Total VAs',
        value: total.toLocaleString(),
        variant: 'default' as const
      },
      {
        label: 'New Today',
        value: todayAccounts.length.toLocaleString(),
        variant: 'default' as const
      },
      {
        label: 'Activated',
        value: activatedAccounts.length.toLocaleString(),
        variant: 'success' as const
      },
      {
        label: 'USD',
        value: usdAccounts.length.toLocaleString(),
        variant: 'default' as const
      },
      {
        label: 'EUR',
        value: eurAccounts.length.toLocaleString(),
        variant: 'default' as const
      },
      {
        label: 'MXN',
        value: mxnAccounts.length.toLocaleString(),
        variant: 'default' as const
      },
      {
        label: 'Customers',
        value: customersWithAccounts.toLocaleString(),
        variant: 'default' as const
      }
    ]
  }, [data, total])

  return {
    data,
    isLoading,
    error,
    filters,
    updateFilters,
    refresh,
    refetch,
    loadMore,
    hasMore,
    exportData,
    kpis,
    total,
    lastUpdatedAt
  }
}
